// src/hooks/useDeviceHeading.ts
//
// Returns the user's current compass heading in degrees clockwise
// from true north (0 = N, 90 = E, 180 = S, 270 = W) or `null` when
// no heading source is available. Used by the map's user-location
// marker to render a Google-Maps-style direction cone.
//
// Source priority:
//   1. `deviceorientationabsolute` — Android Chrome, true heading
//   2. `deviceorientation` with `webkitCompassHeading` — iOS Safari
//   3. GPS-derived `heading` from the geolocation API (only valid
//      while the user is actively moving fast enough for GPS to
//      compute a vector — when standing still, it's null/NaN)
//
// iOS gotcha: as of iOS 13+, accessing motion/orientation requires
// `DeviceOrientationEvent.requestPermission()` and that call only
// succeeds inside a user-gesture handler. We attach a one-shot
// `pointerdown` listener that requests permission on the first user
// tap anywhere in the app, then quietly degrades if permission is
// denied. The fallback to GPS heading still works in that case.

import { useEffect, useRef, useState } from "react";
import { useGeolocation } from "react-use";

type DeviceOrientationEventiOS = typeof DeviceOrientationEvent & {
  requestPermission?: () => Promise<"granted" | "denied">;
};

/** Tracks whether we've already kicked off the iOS permission request
 *  so multiple consumers of this hook don't spam-prompt the user. */
let iosPermissionRequested = false;

export function useDeviceHeading(): number | null {
  const [compassHeading, setCompassHeading] = useState<number | null>(null);
  const lastSetAt = useRef<number>(0);

  // GPS fallback — same options as useLocation but we only care about
  // the heading vector here. When the user is moving (e.g. walking),
  // browsers can derive a heading from successive position fixes.
  const geo = useGeolocation({
    enableHighAccuracy: true,
    timeout: 10000,
    maximumAge: 60000,
  });

  useEffect(() => {
    let cancelled = false;

    const handleOrientation = (event: DeviceOrientationEvent) => {
      if (cancelled) return;
      // iOS: webkitCompassHeading is degrees clockwise from north.
      // Android (deviceorientationabsolute): alpha is degrees
      // counter-clockwise from north when absolute=true; convert to
      // clockwise. If absolute is false we can't reliably derive
      // compass heading; fall through.
      const e = event as DeviceOrientationEvent & {
        webkitCompassHeading?: number;
      };

      let heading: number | null = null;
      if (typeof e.webkitCompassHeading === "number") {
        heading = e.webkitCompassHeading;
      } else if (event.absolute && typeof event.alpha === "number") {
        heading = (360 - event.alpha) % 360;
      }

      if (heading == null || Number.isNaN(heading)) return;

      // Throttle state updates to ~10 Hz. The CSS rotation reads
      // smoothly thanks to its transition; we don't need 60 Hz here.
      const now = performance.now();
      if (now - lastSetAt.current < 100) return;
      lastSetAt.current = now;
      setCompassHeading(heading);
    };

    // Prefer absolute (Android) when available; iOS doesn't fire it
    // but does fire `deviceorientation` with webkitCompassHeading.
    window.addEventListener(
      "deviceorientationabsolute",
      handleOrientation as EventListener
    );
    window.addEventListener("deviceorientation", handleOrientation);

    // iOS permission request — must be inside a user gesture, so we
    // hook it onto the first pointerdown. Once permission is granted
    // (or denied), the listener removes itself.
    const DOE = window.DeviceOrientationEvent as
      | DeviceOrientationEventiOS
      | undefined;
    if (
      !iosPermissionRequested &&
      DOE &&
      typeof DOE.requestPermission === "function"
    ) {
      const requestOnce = async () => {
        if (iosPermissionRequested) return;
        iosPermissionRequested = true;
        try {
          await DOE.requestPermission!();
        } catch {
          // User denied or browser refused — silent fallback to GPS.
        }
      };
      window.addEventListener("pointerdown", requestOnce, {
        once: true,
        passive: true,
      });
    }

    return () => {
      cancelled = true;
      window.removeEventListener(
        "deviceorientationabsolute",
        handleOrientation as EventListener
      );
      window.removeEventListener(
        "deviceorientation",
        handleOrientation
      );
    };
  }, []);

  // Compass takes priority. If we never got a compass reading (e.g.
  // desktop browser, denied permission), fall back to GPS heading
  // when the user is moving.
  if (compassHeading != null) return compassHeading;
  if (
    typeof geo.heading === "number" &&
    !Number.isNaN(geo.heading) &&
    geo.heading >= 0
  ) {
    return geo.heading;
  }
  return null;
}
